<template>
  <teleport
    v-if="dsComponentDetails.version === VersionOptions.LEGACY"
    :to="appendTo"
    :disabled="!appendTo"
  >
    <FadePopTransition
      pop-class="sb-modal"
      :class="containerClasses"
      v-bind="$attrs"
      data-test-id="container"
    >
      <div
        v-if="active"
        class="sb-modal__underlay"
      >
        <SbPanel
          v-click-outside="handleClickOutside"
          class="sb-modal sb-modal__content-wrapper"
          variation="bare"
          :class="classes"
        >
          <SbCloseButton
            v-if="showCloseButton"
            :escape-trigger="escapeTrigger"
            class="sb-modal__close-button"
            data-test-id="close-button"
            @onclose="close"
          />
          <SbLoadingPromiseWrapper
            size="sm"
            :loading-promise="loadingPromise"
            class="sb-modal__loader"
          >
            <!--
                @slot text, components, or HtmlElements as children of the component
              -->
            <slot />
          </SbLoadingPromiseWrapper>
        </SbPanel>
      </div>
    </FadePopTransition>
  </teleport>
  <SbModalNext
    v-else
    :append-to="appendTo"
    :active="active"
    :loading-promise="loadingPromise"
    :escape-trigger="escapeTrigger"
    :click-outside-trigger="clickOutsideTrigger"
    :show-close-button="showCloseButton"
    :show-fullscreen="showFullscreen"
    :variation="variation"
    @close="close"
  >
    <template #default>
      <slot />
    </template>
  </SbModalNext>
</template>

<script>
import SbCloseButton from '@src/components/Modal/SbCloseButton.vue';
import SbPanel from '@src/components/Panel/SbPanel.vue';
import FadePopTransition from '@src/components/Transitions/FadePop.vue';
import SbLoadingPromiseWrapper from '@src/components/Loading/SbLoadingPromiseWrapper.vue';

import ClickOutsideDirectiveMixin from '@src/utils/directives/clickOutsideDirective';
import { versionedComponentsMixin } from '@src/mixins/versionedComponents';
import SbModalNext from '@src/components/Modal/SbModalNext.vue';

/**
 * This is a fullscreen template with an X button. It is a wrapper used in action flows instead of using modals.
 */
export default {
  name: 'SbModal',
  components: { SbCloseButton, SbPanel, FadePopTransition, SbLoadingPromiseWrapper, SbModalNext },
  mixins: [ClickOutsideDirectiveMixin, versionedComponentsMixin],
  props: {
    /**
     * Boolean that determines whether or not to show the modal
     */
    active: {
      type: Boolean,
      required: true,
    },
    /**
     * A promise which will be used to apply loading styles until its resolved/rejected
    */
    loadingPromise: {
      type: Promise,
      default: null,
    },
    /**
     * A boolean that allows the onclose event to be fired via hitting the escape key.
    */
    escapeTrigger: {
      type: Boolean,
      default: true,
    },
    /**
     * A boolean that allows the onclose event to be fired via clicking outside the modal.
    */
    clickOutsideTrigger: {
      type: Boolean,
      default: () => true,
    },
    /**
     * Classes to be applied to the modal panel as the top most element is the underlay.
    */
    classes: {
      type: Array,
      default: () => ([]),
    },
    /**
     * A boolean that allows to show or hide close button.
     */
    showCloseButton: {
      type: Boolean,
      default: true,
    },
    /**
     * A boolean value that allows to show the modal in fullscreen mode.
     */
    showFullscreen: {
      type: Boolean,
      default: false,
    },
    /**
     * A valid query selector or HTML element to specify where the modal gets attached.
     */
    appendTo: {
      type: [String, HTMLElement],
      default: () => undefined,
    },
    /**
     * A string that allows to show the modal in actionSheet or modal mode.
     * `modal, actionSheet`
     */
    variation: {
      type: String,
      default: 'modal',
    },
  },
  emits: [
    /**
    * Emitted with the close event when the template is closed
    */
    'close',
    /**
    * Emitted with `false` when Modal is closed
    */
    'update:active',
  ],
  computed: {
    containerClasses() {
      const classes = [];

      if (this.showFullscreen) {
        classes.push('sb-modal--fullscreen');
      }

      return classes;
    },
  },
  methods: {
    handleClickOutside(e) {
      if (this.clickOutsideTrigger) this.close(e);
    },
    escapeKeyHandler(e) {
      this.close(e);
    },
    close(e) {
      this.$emit('update:active', false);
      this.$emit('close', e);
    },
  },
};
</script>

<style lang='scss' scoped>
@import '@src/components/Modal/style/sb-modal.scss';
</style>
